import apiClient from './client'
import { apiEndpoints, SHOP_STORAGE_BASE_URL } from '@/config/api'

const DEFAULT_PER_PAGE = 12

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const normalizeCategory = (category) => {
  if (!category || typeof category !== 'object') return null

  return {
    ...category,
    slug: category.slug || (category.id ? String(category.id) : null),
    name: category.name || 'Uncategorized',
    description: category.description || '',
    image: category.image || category.image_url || null,
    image_url: category.image_url || category.image || null
  }
}

const normalizeImages = (images, image, galleryUrls, imageUrl) => {
  const merged = []
  
  // Handle different API field names
  const list = Array.isArray(images) ? images : 
               Array.isArray(galleryUrls) ? galleryUrls : []
  const mainImage = image || imageUrl

  if (mainImage) {
    const normalizedMain = convertToLocalImagePath(mainImage)
    if (normalizedMain) merged.push(normalizedMain)
  }

  list.forEach((img) => {
    const normalizedImage = convertToLocalImagePath(img)
    if (normalizedImage && !merged.includes(normalizedImage)) {
      merged.push(normalizedImage)
    }
  })

  return merged
}

const convertToLocalImagePath = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== 'string') return null

  const trimmed = imageUrl.trim()
  if (!trimmed) return null

  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('//')) {
    return trimmed
  }

  if (trimmed.startsWith('/')) {
    return trimmed
  }

  // Use Oliver Laravel storage for all relative paths
  if (trimmed.startsWith('storage/')) {
    return `${SHOP_STORAGE_BASE_URL}/${trimmed.replace(/^storage\//, '')}`
  }

  // Oliver payloads may include relative public-disk paths like `products/file.png`.
  if (trimmed.startsWith('products/')) {
    return `${SHOP_STORAGE_BASE_URL}/${trimmed}`
  }

  return trimmed
}

const normalizeAttributesToSpecifications = (attributes) => {
  if (!Array.isArray(attributes)) return null

  const specs = {}

  attributes.forEach((attribute) => {
    const key = attribute?.name
    const options = Array.isArray(attribute?.options) ? attribute.options.filter(Boolean) : []

    if (key && options.length) {
      specs[key] = options.join(', ')
    }
  })

  return Object.keys(specs).length ? specs : null
}

const normalizePricing = (payload) => {
  const rawPrice = toNumber(payload?.price)
  const rawComparePrice = toNumber(payload?.compare_price)
  const rawSalePrice = toNumber(payload?.sale_price)

  let currentPrice = rawPrice
  let comparePrice = null

  // Source payload shape: price=current, compare_price=original.
  if (rawComparePrice > 0 && rawComparePrice > rawPrice) {
    currentPrice = rawPrice
    comparePrice = rawComparePrice
  // Shop payload shape: price=original, sale_price=current.
  } else if (rawSalePrice > 0 && rawSalePrice < rawPrice) {
    currentPrice = rawSalePrice
    comparePrice = rawPrice
  }

  const onSale = comparePrice !== null && comparePrice > currentPrice

  return {
    price: currentPrice,
    comparePrice: onSale ? comparePrice : null,
    salePrice: onSale ? currentPrice : null,
    onSale
  }
}

const normalizeVariant = (variant) => {
  if (!variant || typeof variant !== 'object') return null

  const pricing = normalizePricing(variant)
  const stockQuantity = toNumber(variant.stock_quantity ?? variant.stock)
  const stockStatus = String(variant.stock_status || '').toLowerCase()

  const inStock = typeof variant.in_stock === 'boolean'
    ? variant.in_stock
    : stockStatus === 'instock' ||
      stockStatus === 'onbackorder' ||
      stockStatus === 'in_stock' ||
      stockStatus === 'on_backorder' ||
      stockQuantity > 0

  // Normalize variant image (handle different API field names)
  const variantImage = convertToLocalImagePath(variant.image || variant.image_url || null)

  return {
    ...variant,
    image: variantImage,
    price: pricing.price,
    compare_price: pricing.comparePrice,
    sale_price: pricing.salePrice,
    on_sale: pricing.onSale,
    stock: stockQuantity,
    stock_quantity: stockQuantity,
    in_stock: inStock,
    attributes: (Array.isArray(variant.attributes) && variant.attributes.length === 0)
      ? {}
      : (variant.attributes || {})
  }
}

/**
 * When variant.attributes is empty but the product has attribute definitions,
 * parse the variant name (e.g. "Red / 6mm") to build an attributes map
 * keyed by the attribute slug.
 */
const inferVariantAttributes = (variants, productAttributes) => {
  if (!Array.isArray(productAttributes) || !productAttributes.length) return variants
  if (!Array.isArray(variants) || !variants.length) return variants

  // Only infer if variants are missing attributes
  const needsInference = variants.every(v => {
    const attrs = v.attributes
    return !attrs || (Array.isArray(attrs) && attrs.length === 0) || (typeof attrs === 'object' && Object.keys(attrs).length === 0)
  })
  if (!needsInference) return variants

  // Build lookup per attribute, sorted longest-option-first so "16" matches before "6"
  const attrLookups = productAttributes.map(attr => {
    const slug = attr.slug || attr.name?.toLowerCase().replace(/\s+/g, '-')
    const options = (Array.isArray(attr.options) ? attr.options : []).filter(Boolean)
    const sorted = [...options].sort((a, b) => String(b).length - String(a).length)
    return { slug, options: sorted }
  })

  // Match option as a whole token in the variant name (word-boundary aware)
  const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const matchesWholeToken = (name, option) => {
    const pattern = escapeRegex(option.toLowerCase())
    const regex = new RegExp(`(?:^|[\\s/|\\-])${pattern}(?:[\\s/|\\-]|$)`)
    return regex.test(name.toLowerCase())
  }

  // Fallback: all words of the option appear in order in the variant name
  // Handles cases like option "4 220 V" matching variant "4 Kw 220 V"
  const matchesAllWords = (name, option) => {
    const nameWords = name.toLowerCase().split(/[\s/|-]+/).filter(Boolean)
    const optionWords = option.toLowerCase().split(/[\s/|-]+/).filter(Boolean)
    if (optionWords.length === 0) return false
    let nameIdx = 0
    for (const word of optionWords) {
      while (nameIdx < nameWords.length && nameWords[nameIdx] !== word) {
        nameIdx++
      }
      if (nameIdx >= nameWords.length) return false
      nameIdx++
    }
    return true
  }

  return variants.map(variant => {
    if (!variant.name) return variant

    const attrs = {}
    for (const { slug, options } of attrLookups) {
      for (const opt of options) {
        if (matchesWholeToken(variant.name, String(opt)) || matchesAllWords(variant.name, String(opt))) {
          attrs[slug] = opt
          break // longest match wins for this attribute
        }
      }
    }

    if (Object.keys(attrs).length > 0) {
      return { ...variant, attributes: attrs }
    }
    return variant
  })
}

const normalizeProduct = (product) => {
  if (!product || typeof product !== 'object') return null

  const pricing = normalizePricing(product)
  const images = normalizeImages(product.images, product.image, product.gallery_urls, product.image_url)

  const hasTrackedInventory = product.stock_management !== 'dont_track'
  const stockQuantity = toNumber(
    product.stock_quantity ??
    product.stock ??
    product?.inventory?.quantity
  )
  const stockStatus = String(product.stock_status || '').toLowerCase()
  const stock = Math.max(stockQuantity, hasTrackedInventory ? 0 : 1)

  const inStock = stockStatus === 'instock' ||
      stockStatus === 'onbackorder' ||
      stockStatus === 'in_stock' ||
      stockStatus === 'on_backorder' ||
      (Boolean(product.allow_backorder) && stockStatus !== 'outofstock' && stockStatus !== 'out_of_stock') ||
      (typeof product.in_stock === 'boolean' ? product.in_stock : stock > 0)

  const rawVariants = Array.isArray(product.variants)
    ? product.variants.map(normalizeVariant).filter(Boolean)
    : []

  const variants = inferVariantAttributes(rawVariants, product.attributes)

  // DEBUG: log variant data to diagnose greyed-out issue
  if (rawVariants.length > 0) {
    console.log('[DEBUG normalizeProduct]', {
      productName: product.name,
      productAttributes: JSON.stringify(product.attributes),
      rawVariantSample: rawVariants.slice(0, 2).map(v => ({ name: v.name, attributes: v.attributes, in_stock: v.in_stock, stock: v.stock })),
      inferredVariantSample: variants.slice(0, 2).map(v => ({ name: v.name, attributes: v.attributes, in_stock: v.in_stock, stock: v.stock })),
    })
  }

  return {
    ...product,
    category: normalizeCategory(product.category),
    price: pricing.price,
    compare_price: pricing.comparePrice,
    sale_price: pricing.salePrice,
    on_sale: pricing.onSale,
    image: images[0] || null,
    images,
    in_stock: inStock,
    stock,
    stock_quantity: stock,
    rating: toNumber(product.average_rating),
    reviews_count: Number(product.rating_count) || 0,
    description: product.description || product.short_description || '',
    specifications: product.specifications || normalizeAttributesToSpecifications(product.attributes),
    variants,
  }
}

const normalizeMeta = (meta, count = 0, page = 1, perPage = DEFAULT_PER_PAGE) => {
  if (meta && typeof meta === 'object') {
    const currentPage = Math.max(1, toNumber(meta.current_page, page))
    const normalizedPerPage = Math.max(1, toNumber(meta.per_page, perPage))
    const total = Math.max(0, toNumber(meta.total, count))

    return {
      ...meta,
      current_page: currentPage,
      per_page: normalizedPerPage,
      total,
      last_page: Math.max(1, toNumber(meta.last_page, Math.ceil(total / normalizedPerPage) || 1))
    }
  }

  const normalizedPerPage = Math.max(1, toNumber(perPage, DEFAULT_PER_PAGE))

  return {
    current_page: Math.max(1, toNumber(page, 1)),
    last_page: count > 0 ? Math.ceil(count / normalizedPerPage) : 1,
    per_page: normalizedPerPage,
    total: count
  }
}

const unwrapCollection = (payload) => {
  if (Array.isArray(payload)) {
    return { items: payload, meta: null }
  }

  if (payload && Array.isArray(payload.data)) {
    return { items: payload.data, meta: payload.meta || null }
  }

  if (payload?.data && Array.isArray(payload.data.data)) {
    return { items: payload.data.data, meta: payload.data.meta || payload.meta || null }
  }

  return { items: [], meta: payload?.meta || null }
}

const normalizeProductsResponse = (payload, fallbackPage = 1, fallbackPerPage = DEFAULT_PER_PAGE) => {
  const { items, meta } = unwrapCollection(payload)
  const data = items.map(normalizeProduct).filter(Boolean)

  return {
    data,
    meta: normalizeMeta(meta, data.length, fallbackPage, fallbackPerPage)
  }
}

const normalizeCategoryResponse = (payload) => {
  const list = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.data?.data)
      ? payload.data.data
    : Array.isArray(payload)
      ? payload
      : []

  return {
    data: list.map(normalizeCategory).filter(Boolean)
  }
}

const buildProductsParams = (params = {}) => {
  const query = { ...params }

  if (query.limit && !query.per_page) {
    query.per_page = query.limit
  }

  if (query.category && !query.category_id) {
    query.category_id = query.category
  }

  delete query.limit
  delete query.category

  return query
}

const isNotFound = (error) => error?.response?.status === 404

const buildLocalRelatedProducts = async (product) => {
  if (!product?.slug) return []

  const response = await apiClient.get(apiEndpoints.products.list, {
    params: {
      category_slug: product?.category?.slug,
      per_page: 24,
      sort: 'created_at',
      order: 'desc'
    }
  })

  const normalized = normalizeProductsResponse(response.data, 1, 24).data
  return normalized.filter((item) => item.slug !== product.slug).slice(0, 4)
}

export const productsApi = {
  /**
   * Get list of products with filters
   * @param {Object} params - Filter parameters (search, category_slug, min_price, max_price, in_stock, featured, sort, per_page)
   * @returns {Promise} Products list with pagination meta
   */
  async getProducts(params = {}) {
    const query = buildProductsParams(params)
    const page = query.page || 1
    const perPage = query.per_page || DEFAULT_PER_PAGE

    const response = await apiClient.get(apiEndpoints.products.list, { params: query })
    return normalizeProductsResponse(response.data, page, perPage)
  },

  /**
   * Get all products (fetches all pages)
   * @param {Object} params - Filter parameters (excluding pagination)
   * @returns {Promise} All products array
   */
  async getAllProducts(params = {}) {
    const query = buildProductsParams(params)
    delete query.page
    delete query.per_page

    // First request to get total count
    const firstResponse = await apiClient.get(apiEndpoints.products.list, {
      params: { ...query, per_page: 100, page: 1 }
    })
    const firstPage = normalizeProductsResponse(firstResponse.data, 1, 100)
    let allProducts = [...firstPage.data]

    // Fetch remaining pages if any
    const totalPages = firstPage.meta.last_page
    if (totalPages > 1) {
      const pagePromises = []
      for (let page = 2; page <= totalPages; page++) {
        pagePromises.push(
          apiClient.get(apiEndpoints.products.list, {
            params: { ...query, per_page: 100, page }
          })
        )
      }

      const responses = await Promise.all(pagePromises)
      responses.forEach((response, index) => {
        const normalized = normalizeProductsResponse(response.data, index + 2, 100)
        allProducts = allProducts.concat(normalized.data)
      })
    }

    return {
      data: allProducts,
      meta: {
        total: allProducts.length,
        current_page: 1,
        last_page: 1,
        per_page: allProducts.length
      }
    }
  },

  /**
   * Get products for dropdown/navigation (products marked with show_in_dropdown)
   * @param {number|null} limit - Optional number of products to fetch
   * @returns {Promise} Products array
   */
  async getDropdownProducts(limit = null) {
    const parsedLimit = Number(limit)
    const hasLimit = Number.isFinite(parsedLimit) && parsedLimit > 0
    const params = hasLimit ? { limit: Math.trunc(parsedLimit) } : {}

    const response = await apiClient.get(apiEndpoints.products.dropdown, { params })
    return normalizeProductsResponse(response.data, 1, hasLimit ? Math.trunc(parsedLimit) : DEFAULT_PER_PAGE)
  },

  /**
   * Get featured products for homepage
   * @returns {Promise} Featured products array
   */
  async getFeatured() {
    try {
      const response = await apiClient.get(apiEndpoints.products.featured)
      return normalizeProductsResponse(response.data, 1, DEFAULT_PER_PAGE)
    } catch (error) {
      if (!isNotFound(error)) {
        throw error
      }

      const fallback = await productsApi.getProducts({ per_page: DEFAULT_PER_PAGE, sort: 'created_at', order: 'desc' })
      return {
        ...fallback,
        data: fallback.data.filter((product) => product.is_featured).slice(0, DEFAULT_PER_PAGE)
      }
    }
  },

  /**
   * Get single product by slug
   * @param {string} slug - Product slug
   * @returns {Promise} Product details
   */
  async getProduct(slug) {
    const response = await apiClient.get(apiEndpoints.products.detail(slug))
    const product = normalizeProduct(response.data?.data || response.data)
    const relatedList = Array.isArray(response.data?.related) ? response.data.related : []
    let related = relatedList.map(normalizeProduct).filter(Boolean)

    if (!related.length && product?.category?.slug) {
      try {
        related = await buildLocalRelatedProducts(product)
      } catch {
        related = []
      }
    }

    return {
      data: product,
      related
    }
  },

  /**
   * Get product variants by product ID or slug
   * @param {string|number} idOrSlug - Product ID or slug
   * @returns {Promise} Variants array
   */
  async getVariants(idOrSlug) {
    try {
      const response = await apiClient.get(apiEndpoints.products.variants(idOrSlug))
      const variants = Array.isArray(response.data?.data) 
        ? response.data.data 
        : Array.isArray(response.data) 
          ? response.data 
          : []
      return {
        data: variants.map(normalizeVariant).filter(Boolean)
      }
    } catch (error) {
      console.error('Failed to fetch variants:', error)
      return { data: [] }
    }
  },

  /**
   * Get related products
   * @param {string|number} idOrSlug - Product ID or slug
   * @returns {Promise} Related products array
   */
  async getRelated(idOrSlug) {
    try {
      const response = await apiClient.get(apiEndpoints.products.related(idOrSlug))
      return normalizeProductsResponse(response.data, 1, DEFAULT_PER_PAGE)
    } catch (error) {
      if (!isNotFound(error)) {
        throw error
      }

      const product = await productsApi.getProduct(idOrSlug)
      return {
        data: product.related || [],
        meta: normalizeMeta(null, (product.related || []).length)
      }
    }
  },

  /**
   * Get all categories
   * @returns {Promise} Categories array
   */
  async getCategories() {
    const response = await apiClient.get(apiEndpoints.categories.list)
    return normalizeCategoryResponse(response.data)
  },

  /**
   * Get category by slug
   * @param {string} slug - Category slug
   * @returns {Promise} Category details
   */
  async getCategory(slug) {
    const response = await apiClient.get(apiEndpoints.categories.detail(slug))
    return {
      data: normalizeCategory(response.data?.data || response.data)
    }
  },

  /**
   * Get products in category (with pagination)
   * @param {string} slug - Category slug
   * @param {Object} params - Filter parameters
   * @returns {Promise} Products list with pagination meta
   */
  async getCategoryProducts(slug, params = {}) {
    const query = buildProductsParams(params)
    const page = query.page || 1
    const perPage = query.per_page || DEFAULT_PER_PAGE

    try {
      const response = await apiClient.get(apiEndpoints.categories.products(slug), { params: query })
      return normalizeProductsResponse(response.data, page, perPage)
    } catch (error) {
      if (!isNotFound(error)) {
        throw error
      }

      return productsApi.getProducts({
        ...query,
        category_slug: slug
      })
    }
  },

  /**
   * Get shop products from Oliver Laravel API (with filters/pagination)
   * @param {Object} params - Filter parameters
   * @returns {Promise} Products list with pagination meta
   */
  async getShopProducts(params = {}) {
    const query = buildProductsParams(params)
    const page = query.page || 1
    const perPage = query.per_page || DEFAULT_PER_PAGE

    const response = await apiClient.get(apiEndpoints.shopProducts.list, { params: query })
    return normalizeProductsResponse(response.data, page, perPage)
  },

  /**
   * Get all shop categories
   * @returns {Promise} Categories array with total product count
   */
  async getShopCategories() {
    const response = await apiClient.get(apiEndpoints.shopCategories.list, {
      params: { simple: 1 }
    })
    const normalized = normalizeCategoryResponse(response.data)
    normalized.total = toNumber(
      response.data?.total ??
      response.data?.meta?.total ??
      response.data?.pagination?.total,
      0
    )
    return normalized
  },

  /**
   * Get shop category by slug
   * @param {string} slug - Category slug
   * @returns {Promise} Category details
   */
  async getShopCategory(slug) {
    const response = await apiClient.get(apiEndpoints.shopCategories.detail(slug))
    return {
      data: normalizeCategory(response.data?.data || response.data)
    }
  },

  /**
   * Get products in a shop category (with pagination)
   * @param {string} slug - Shop category slug
   * @param {Object} params - Filter parameters
   * @returns {Promise} Products list with pagination meta
   */
  async getShopCategoryProducts(slug, params = {}) {
    const query = buildProductsParams(params)
    const page = query.page || 1
    const perPage = query.per_page || DEFAULT_PER_PAGE

    try {
      const response = await apiClient.get(apiEndpoints.shopCategories.products(slug), { params: query })
      return normalizeProductsResponse(response.data, page, perPage)
    } catch (error) {
      if (!isNotFound(error)) {
        throw error
      }

      return productsApi.getProducts({
        ...query,
        category_slug: slug
      })
    }
  },
}

export default productsApi
